import React from 'react';
import ActionButton from '../../components/ui/ActionButton';
import DataTable, { Column } from '../../components/ui/DataTable';
import {
  UploadRecord,
  UploadStatus,
  MappingConfig,
  DELETABLE_STATUSES,
  StatusBadge,
  selectStyle,
  labelStyle,
} from './types';
import PipelineProgress from './PipelineProgress';

interface GLUploadTabProps {
  entities: any[];
  glEntity: string;
  setGlEntity: (v: string) => void;
  glYear: number;
  setGlYear: (v: number) => void;
  glMonth: number;
  setGlMonth: (v: number) => void;
  glMappingId: number | null;
  setGlMappingId: (v: number | null) => void;
  glFile: File | null;
  setGlFile: (f: File | null) => void;
  glReplaceExisting: boolean;
  setGlReplaceExisting: (v: boolean) => void;
  glUploading: boolean;
  glDragOver: boolean;
  setGlDragOver: (v: boolean) => void;
  glInputRef: React.RefObject<HTMLInputElement | null>;
  mappings: MappingConfig[];
  handleGlUpload: () => void;
  handleDrag: (e: React.DragEvent, setOver: (v: boolean) => void, over: boolean) => void;
  handleDrop: (e: React.DragEvent, setFile: (f: File | null) => void, setOver: (v: boolean) => void) => void;
  // Pipeline progress
  activeUpload: UploadStatus | null;
  showRejected: boolean;
  setShowRejected: (v: boolean) => void;
  setActiveUpload: (v: UploadStatus | null) => void;
  // Upload history
  uploads: UploadRecord[];
  uploadsLoading: boolean;
  // Delete
  deleteTarget: UploadRecord | null;
  setDeleteTarget: (v: UploadRecord | null) => void;
  deleting: boolean;
  handleDeleteUpload: () => void;
}

export default function GLUploadTab({
  entities,
  glEntity,
  setGlEntity,
  glYear,
  setGlYear,
  glMonth,
  setGlMonth,
  glMappingId,
  setGlMappingId,
  glFile,
  setGlFile,
  glReplaceExisting,
  setGlReplaceExisting,
  glUploading,
  glDragOver,
  setGlDragOver,
  glInputRef,
  mappings,
  handleGlUpload,
  handleDrag,
  handleDrop,
  activeUpload,
  showRejected,
  setShowRejected,
  setActiveUpload,
  uploads,
  uploadsLoading,
  deleteTarget,
  setDeleteTarget,
  deleting,
  handleDeleteUpload,
}: GLUploadTabProps) {

  const historyColumns: Column[] = [
    { key: 'uploaded_at', label: 'Date', render: (v: any) => v ? <span style={{ whiteSpace: 'nowrap', fontSize: 11 }}>{new Date(v).toLocaleString()}</span> : '-' },
    { key: 'entity_code', label: 'Entity', render: (v: any) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'period', label: 'Period' },
    { key: 'filename', label: 'File', render: (v: any) => <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>{v}</span> },
    { key: 'status', label: 'Status', render: (v: any, row: any) => (
      <>
        <StatusBadge status={v} />
        {row.is_superseded && <span style={{ fontSize: 9, color: 'var(--text-tertiary)', marginLeft: 4 }}>SUPERSEDED</span>}
      </>
    )},
    { key: 'rows_ingested', label: 'Rows', align: 'right', render: (v: any) => <span style={{ fontFamily: 'var(--font-mono)' }}>{v ?? '-'}</span> },
    { key: 'rows_rejected', label: 'Rejected', align: 'right', render: (v: any) => <span style={{ fontFamily: 'var(--font-mono)', color: (v || 0) > 0 ? '#fca5a5' : 'var(--text-tertiary)' }}>{v ?? '-'}</span> },
    { key: 'exceptions_detected', label: 'Exceptions', align: 'right', render: (v: any) => <span style={{ fontFamily: 'var(--font-mono)' }}>{v ?? '-'}</span> },
    { key: 'processing_time_ms', label: 'Time', align: 'right', render: (v: any) => (
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
        {v == null ? '\u2014' : v < 1000 ? `${v}ms` : v < 60000 ? `${(v / 1000).toFixed(1)}s` : `${Math.floor(v / 60000)}m ${Math.round((v % 60000) / 1000)}s`}
      </span>
    )},
    { key: 'uploaded_by', label: 'Uploaded By', render: (v: any) => <span style={{ fontSize: 11, color: 'var(--text-tertiary)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block' }}>{v}</span> },
    { key: '_actions', label: '', width: 40, render: (_: any, row: any) => (
      DELETABLE_STATUSES.has(row.status) && !row.is_superseded ? (
        <button
          onClick={(e) => { e.stopPropagation(); setDeleteTarget(row); }}
          title="Delete upload"
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: 4,
            color: '#ef4444', opacity: 0.7, transition: 'opacity 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
          onMouseLeave={e => (e.currentTarget.style.opacity = '0.7')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
        </button>
      ) : null
    )},
  ];

  return (
    <>
      {/* Upload form */}
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-default)',
        borderRadius: 8, padding: 20, marginBottom: 20,
      }}>
        {/* Entity / Period / Mapping row */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={labelStyle}>Entity</label>
            <select value={glEntity} onChange={e => setGlEntity(e.target.value)} style={selectStyle}>
              <option value="">Select entity...</option>
              {entities.map((ent: any) => (
                <option key={ent.entity_code} value={ent.entity_code}>
                  {ent.entity_code} - {ent.entity_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Year</label>
            <input
              type="number"
              value={glYear}
              onChange={e => setGlYear(parseInt(e.target.value) || 2025)}
              style={{ ...selectStyle, width: 80 }}
            />
          </div>
          <div>
            <label style={labelStyle}>Month</label>
            <select value={glMonth} onChange={e => setGlMonth(parseInt(e.target.value))} style={selectStyle}>
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {String(i + 1).padStart(2, '0')} - {new Date(2025, i, 1).toLocaleString('en', { month: 'short' })}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Mapping</label>
            <select value={glMappingId ?? ''} onChange={e => setGlMappingId(e.target.value ? parseInt(e.target.value) : null)} style={selectStyle}>
              <option value="">Auto-detect (default columns)</option>
              {mappings.map(m => (
                <option key={m.id} value={m.id}>{m.name}{m.is_default ? ' (default)' : ''}</option>
              ))}
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-secondary)', cursor: 'pointer', paddingBottom: 4 }}>
            <input
              type="checkbox"
              checked={glReplaceExisting}
              onChange={e => setGlReplaceExisting(e.target.checked)}
            />
            Replace existing period data
          </label>
        </div>

        {/* Drop zone */}
        <div
          onDragEnter={e => handleDrag(e, setGlDragOver, glDragOver)}
          onDragOver={e => handleDrag(e, setGlDragOver, glDragOver)}
          onDragLeave={e => handleDrag(e, setGlDragOver, glDragOver)}
          onDrop={e => handleDrop(e, setGlFile, setGlDragOver)}
          onClick={() => glInputRef.current?.click()}
          style={{
            border: `2px dashed ${glDragOver ? 'var(--accent)' : 'var(--border-subtle)'}`,
            borderRadius: 8, padding: '32px 20px', textAlign: 'center', cursor: 'pointer',
            background: glDragOver ? 'var(--accent-subtle)' : 'transparent',
            transition: 'all 0.15s ease',
            marginBottom: 16,
          }}
        >
          <input
            ref={glInputRef}
            type="file"
            accept=".csv,.xlsx"
            onChange={e => setGlFile(e.target.files?.[0] || null)}
            style={{ display: 'none' }}
          />
          {glFile ? (
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{glFile.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                {(glFile.size / 1024).toFixed(1)} KB
                <span style={{ marginLeft: 8, cursor: 'pointer', color: '#ef4444' }}
                  onClick={e => { e.stopPropagation(); setGlFile(null); }}>
                  Remove
                </span>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                Drag & drop CSV or Excel file, or click to browse
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4 }}>
                Max 50MB. Supported: .csv, .xlsx
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8 }}>
          <ActionButton
            label={glUploading ? 'Uploading...' : 'Upload & Process'}
            onClick={() => handleGlUpload()}
            variant="primary"
            disabled={!glFile || !glEntity || glUploading}
          />
        </div>
      </div>

      {/* Processing progress */}
      {activeUpload && (
        <PipelineProgress
          activeUpload={activeUpload}
          showRejected={showRejected}
          setShowRejected={setShowRejected}
          setActiveUpload={setActiveUpload}
        />
      )}

      {/* Upload History */}
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>Upload History</div>
        <DataTable
          columns={historyColumns}
          rows={uploads.map(u => ({ ...u, _opacity: u.is_superseded ? 0.5 : 1 }))}
          loading={uploadsLoading}
          emptyMessage="No uploads yet. Upload a GL file above to get started."
          rowKey="id"
        />
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }} onClick={() => !deleting && setDeleteTarget(null)}>
          <div
            style={{
              background: 'var(--bg-card)', border: '1px solid var(--border-default)',
              borderRadius: 10, padding: 24, maxWidth: 440, width: '90%',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ color: '#ef4444', fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>Delete Upload</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.5, margin: '0 0 20px' }}>
              Delete upload <strong style={{ color: 'var(--text-primary)' }}>{deleteTarget.filename}</strong>?
              This will remove all associated journal entries, exceptions, and audit data.
              <span style={{ color: '#ef4444', fontWeight: 600 }}> This cannot be undone.</span>
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <ActionButton
                label="Cancel"
                onClick={() => setDeleteTarget(null)}
                variant="secondary"
                disabled={deleting}
              />
              <ActionButton
                label={deleting ? 'Deleting...' : 'Delete'}
                onClick={() => handleDeleteUpload()}
                variant="danger"
                disabled={deleting}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
